<template>
  <div>
    <breadcrumbs :home="product.name + ': Adjust inventory'"/>

    <v-card flat>
      <v-form v-model="valid" ref="form" :disabled="!edit">
        <v-container class="pt-6 px-6 purtrak-scrollable" fluid>

          <v-row no-gutters>
            <v-col cols="6" sm="4" lg="3">
              <date-picker v-model="transaction.date" @change="refresh"/>
            </v-col>
          </v-row>

          <v-row class="mb-4">
            <v-col>
              <in-bond v-model="transaction.bonded" @change="changeBond"/>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="6" sm="3" lg="2"  v-if="company.settings.lotTrackingForProducts && (transaction.bonded || company.settings.lotTrackingForDutyPaid)">
              <v-combobox v-model="transaction.lot" outlined dense label="Lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]" @change="setAdjustedValues()"/>
            </v-col>
            <v-col cols="12" sm="6" lg="4" v-if="sites.length>1">
              <v-autocomplete outlined dense label="Site" v-model="transaction.siteId" :items="sites" item-value="id" item-text="name" @change="setAdjustedValues()"/>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-radio-group v-model="transaction.specifyDifference" dense class="mt-0" row @change="setAdjustedValues()">
                <v-radio label="Adjust by difference" :value="true"/>
                <v-radio label="Adjust to specified quantity/value" :value="false"/>
              </v-radio-group>
            </v-col>
          </v-row>

          <v-row v-if="transaction.specifyDifference">
            <v-col cols="3" sm="2" lg="1">
              <v-btn-toggle mandatory dense v-model="negative"
                @change="calculateValue = negative; transaction.tryDutyPaidOnIncrease = !negative">
                <v-btn icon :disabled="!edit" :value="false"><v-icon small>mdi-plus</v-icon></v-btn>
                <v-btn icon :disabled="!edit" :value="true"><v-icon small>mdi-minus</v-icon></v-btn>
              </v-btn-toggle>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <decimal-number-input label="Cases" v-model="cases" :precision="0" integer prefix="" suffix=""
                                    :hint="availableCases" persistent-hint/>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <decimal-number-input :label="units" v-model="bottles" :precision="0" integer prefix="" suffix=""
                                    :hint="availableBottles" persistent-hint/>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <decimal-number-input label="Total value" v-model="value" :disabled="calculateValue"/>
            </v-col>
            <v-col cols="6" sm="3" lg="2">
              <v-checkbox label="Calculate value" v-model="calculateValue" dense class="mt-1"/>
            </v-col>
          </v-row>
          <template v-else>
            <v-row>
              <v-col cols="6" sm="3" lg="2">
                <decimal-number-input label="Adjusted cases" v-model="transaction.cases" :precision="0" integer prefix="" suffix=""/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <decimal-number-input :label="`Adjusted ${units}`" v-model="transaction.bottles" :precision="0" integer prefix="" suffix=""/>
              </v-col>
              <v-col cols="6" sm="3" lg="2">
                <decimal-number-input label="Total adjusted value" v-model="transaction.value"/>
              </v-col>
            </v-row>
            <v-row>
              <v-col class="text-caption mb-6">Adjustment will be saved as "Adjust by difference" with calculated difference</v-col>
            </v-row>
          </template>

          <v-row>
            <v-col cols="9" sm="4" md="3" v-if="transaction.bonded">
              <v-tooltip bottom max-width="300" open-delay="1000" :nudge-top="30">
                <template v-slot:activator="{on}">
                  <div v-on="on">
                    <v-select v-model="transaction.tryDutyPaidOnIncrease" outlined dense label="Excise handling"
                              :disabled="negative"
                              :items="[{value:true,text:'Duty-paid'},{value:false,text:'Inventory adjustment'}]"/>
                  </div>
                </template>
                <template v-if="transaction.tryDutyPaidOnIncrease">
                  Changes in inventory will be handled as duty-paid reductions with increases offseting reductions.
                  If total increases exceed total duty-paid reductions <em>when this transaction is posted</em>,
                  then the difference is attributed to inventory adjustments (line F on B262).
                </template>
                <template v-else>
                  Changes in inventory are attributed to inventory adjustments (line F on B262).
                  All adjusting entries are subject to audit.
                </template>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-row v-if="values && company.connectedToAccounting">
            <v-col cols="12" sm="6" lg="4">
              <account-picker v-model="transaction.expenseAccountRef" label="Inventory adjustment account"
                              :accounts="accounts"
                              hint="An account to balance the inventory asset account. Usually an inventory shrinkage expense or cost of goods account."/>
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255),commentsRequired]" counter="255"/>
            </v-col>
          </v-row>

        </v-container>
      </v-form>
      <v-divider/>
      <v-card-actions class="px-6">
        <template v-if="transactionId && company.permissions.includes('inventory.write')">
          <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                    v-model="edit" label="Edit" hide-details class="mt-0"/>
          <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
        </template>
        <v-spacer/>
        <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
        <v-btn v-if="edit" color="primary" :loading="saving" @click="save">Save</v-btn>
      </v-card-actions>
    </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import InBond from '@/components/InBond'

export default {
  name: 'InventoryProductsAdjustInventory',
  mixins: [confirmLeave],
  components: {InBond},
	props: {
    productId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      init: true,
      edit: false,
      product: {name:'',sites:[]},
      valid:true,
      saving:false,
      accounts: [],
      calculateValue: false,
      cases: 0,
      bottles: 0,
      value: 0,
      negative: false,
      transaction: {productId:this.productId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), bonded:null,
                    specifyDifference:true, cases:0, bottles: 0, value:null, lot:'', comments: '',expenseAccountRef:'',siteId:'',
                    tryDutyPaidOnIncrease:true},
      commentsRequired: v => !this.transaction.bonded || !!v || 'Required',
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.transactionId, this.transaction.bonded?{B262:true}:undefined);
    },
    values() {
      return this.$store.getters.company.permissions.includes('values') && this.$store.getters.company.settings.useAccounts;
    },
    units() {
      if (this.product.mixedPack)
        return 'Packs';
      if (this.product.containerType === 'Can')
        return 'Cans';
      return 'Bottles';
    },
    currentLots() {
      return this.product.sites.filter(site => site.siteId === this.transaction.siteId &&
                                              (this.transaction.bonded && site.inBondBottles || !this.transaction.bonded && site.dutyPaidBottles))
                                .map(site => site.lot);
    },
    availableTotalBottles() {
      let site;
      if (!this.company.settings.lotTrackingForProducts ||
          !this.company.settings.lotTrackingForDutyPaid && !this.transaction.bonded)
        site = this.product.sites.reduce((site, productSite) => {
          if (productSite.siteId === this.transaction.siteId) {
            site.dutyPaidBottles += productSite.dutyPaidBottles;
            site.inBondBottles += productSite.inBondBottles;
          }
          return site;
        }, {dutyPaidBottles:0, inBondBottles:0});
      else
        site = this.product.sites.find(site => site.siteId === this.transaction.siteId && site.lot === this.transaction.lot);
      return (this.transaction.bonded ? site?.inBondBottles : site?.dutyPaidBottles) || 0;
    },
    availableCases() {
      return this.transactionId ? '' : (this.negative ? Math.floor(this.availableTotalBottles / this.product.caseSize) + ' available' : '');
    },
    availableBottles() {
      return this.transactionId ? '' : (this.negative ? this.availableTotalBottles - Math.floor(this.availableTotalBottles / this.product.caseSize) * this.product.caseSize + ' available' : '');
    }
  },
  watch: {
    calculateValue() {
      if (this.calculateValue)
        this.transaction.value = null;
    },
    bottles() {
      this.dirty = true;
    },
    value() {
      this.dirty = true;
    },
    cases() {
      this.dirty = true;
    },
    negative() {
      this.dirty = true;
    },
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    const results = await this.$apiCall(`query InventoryProductsAdjustInventory($currency:String!,$id:ID!,$edit:Boolean!) {
                                              accounts(currency:$currency) {
                                                ref,parentRef,name,type,typeName
                                              }
                                              productAdjustInventory(id:$id) @include(if:$edit) {
                                                id,productId,bonded,date,specifyDifference,cases,bottles,lot,value,comments,expenseAccountRef,siteId,tryDutyPaidOnIncrease
                                              }
                                              companyAccounts @skip(if:$edit) {inventoryAdjustment}
                                            }`,
                                        {currency:this.company.settings.homeCurrency,
                                         id:this.transactionId||'',edit:this.transactionId!=undefined});
    if (results.error) {
      this.$nextTick(() => this.dirty = false);
      this.$router.back();
      return;
    }
    this.accounts = results.data.accounts;
    if (this.transactionId) {
      this.transaction = results.data.productAdjustInventory;
      this.value = Math.abs(this.transaction.value || 0);
      this.calculateValue = this.transaction.value == null;
      this.negative = this.transaction.bottles < 0;
      this.bottles = Math.abs(this.transaction.bottles);
      this.cases = Math.abs(this.transaction.cases);
    }
    else {
      this.transaction.expenseAccountRef = results.data.companyAccounts.inventoryAdjustment;
      this.transaction.siteId = this.sites[0].id;
      this.edit = true;
    }
    this.init = false;
    await this.refresh();
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    changeBond() {
      if (!this.sites.some(site => site.id === this.transaction.siteId))
        this.transaction.siteId = this.sites[0].id;
      this.$nextTick(() => this.$refs.form.validate());
    },
    setAdjustedValues() {
      if (!this.specifyDifference) {
        const from = this.product.sites.find(site => site.siteId === this.transaction.siteId && site.lot === this.transaction.lot) ?? {cases:0,inBondBottles:0,inBondValue:0,dutyPaidBottles:0,dutyPaidValue:0};
        this.transaction.cases = Math.floor((this.bonded ? from.inBondBottles : from.dutyPaidBottles) / this.product.caseSize);
        this.transaction.bottles = (this.bonded ? from.inBondBottles : from.dutyPaidBottles) - this.transaction.cases * this.product.caseSize;
        this.transaction.value = this.bonded ? from.inBondValue : from.dutyPaidValue;
      }
    },
    async refresh() {
      const results = await this.$apiCall(`query InventoryProductsAdjustInventory($id:ID!,$asof:DateTime!) {
                                                product(id:$id) {
                                                  id,name,caseSize,mixedPack,containerType
                                                  sites(asof:$asof) {siteId,lot,inBondBottles,dutyPaidBottles,inBondValue,dutyPaidValue}
                                                }
                                              }`,
                                          {id:this.transaction ? this.transaction.productId : this.productId,
                                           asof:this.transaction.date});
      if (!results.error) {
        this.product = results.data.product;
        this.setAdjustedValues();
        this.$nextTick(() => {this.$refs.form.validate()});
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      this.$refs.form.validate();
      if (!this.valid) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      const transaction = {...this.transaction};
      if (transaction.specifyDifference)
        Object.assign(transaction, {value:this.calculateValue ? null : (this.negative?-this.value:this.value),
                                    cases:this.negative?-this.cases:this.cases,bottles:this.negative?-this.bottles:this.bottles});
      this.saving = true;
      const results = await this.$apiCall(`mutation InventoryProductsAdjustInventory($input: ProductAdjustInventoryInput!) {
                                            productAdjustInventoryCreate(input:$input) {id}
                                          }`,
                                          {input:transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} adjust inventory transaction for ${this.product.name}`});
      }
		}
	}
}
</script>